import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Avatar, Grid, Chip, Button, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/Block";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FALLBACK_PROFILE = {
  fullName: "Nalini S",
  employeeId: "TCH409",
  email: "nalini.s@cummingstamilschool.org",
  assignedEmail: "nalini.s@school.org",
  personalEmail: "nalini.personal@gmail.com",
  phone: "+1 (678) 555-0199",
  role: "teacher",
  classAssigned: "Nilai08",
  section: "B",
  subject: "Tamil",
  attendanceToday: "Marked",
  lastMarkedDate: "2026-01-15",
  totalStudents: 31,
  pendingTasks: 2,
  classAverage: "82%",
  reportsSubmitted: 8,
  pendingReports: 2,
  googleClassroomUrl: "https://classroom.google.com",
  notebookUrl: "#",
  textbookUrl: "#",
  lessonPlanUrl: "#",
  studyMaterialsUrl: "#",
  joiningDate: "2022-08-01",
  lastLogin: "2026-04-27",
  photo: null,
};

function CircularScore({ value, label }) {
  const numVal = parseInt(value) || 82;
  return (
    <Box sx={{ position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={64}
          thickness={5}
          sx={{ color: "#f0f0f0", position: "absolute" }}
        />
        <CircularProgress
          variant="determinate"
          value={numVal}
          size={64}
          thickness={5}
          sx={{ color: "#244C77" }}
        />
        <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: "bold", color: "#1a1a2e" }}>{value}</Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>{label}</Typography>
    </Box>
  );
}

const FALLBACK_AUDIT = [
  { updatedAt: "2026-04-27T10:15:00Z", updatedBy: "Admin",   changedFields: ["phone", "personalEmail"], note: "Contact details updated" },
  { updatedAt: "2026-03-10T08:30:00Z", updatedBy: "Nalini S",changedFields: ["lessonPlanUrl"],           note: "Lesson plan URL refreshed" },
  { updatedAt: "2026-02-05T14:00:00Z", updatedBy: "Admin",   changedFields: ["section"],                note: "Section reassigned" },
];

const TeacherProfile = () => {
  const [profile, setProfile] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const teacherId = sessionStorage.getItem("userId") || sessionStorage.getItem("applicationId");
      if (!teacherId) { setProfile(FALLBACK_PROFILE); return; }
      const res = await fetchData("teacher/dashboard/profile");
      if (res?.data) setProfile(res.data);
      else setProfile(FALLBACK_PROFILE);
    } catch (_) { setProfile(FALLBACK_PROFILE); }

    // Audit log — §36
    try {
      const logRes = await fetchData("teacher/profile/audit-log");
      const entries = logRes?.data || [];
      setAuditLog(Array.isArray(entries) ? entries : FALLBACK_AUDIT);
    } catch (_) {
      setAuditLog(FALLBACK_AUDIT);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDisable = async () => {
    try {
      await putData("teacher/profile/disable", {});
      setToast({ open: true, severity: "success", message: "Profile disabled successfully." });
      setDisableDialogOpen(false);
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Failed to disable profile." });
      setDisableDialogOpen(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    window.location.href = "/";
  };

  const p = profile || FALLBACK_PROFILE;
  const initials = p.fullName
    ? p.fullName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "NS";

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" }).toUpperCase(); }
    catch (_) { return d; }
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Pages / My Profile
      </p>

      {/* ─── HEADER BANNER ─── */}
      <Paper elevation={0} sx={{ borderRadius: "16px", overflow: "hidden", mb: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
        <Box sx={{ background: "linear-gradient(135deg,#1a237e 0%,#283593 50%,#4527a0 100%)", p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Avatar
              src={p.photo || undefined}
              sx={{ width: 80, height: 80, border: "3px solid rgba(255,255,255,0.5)", fontSize: "26px", fontWeight: "bold", backgroundColor: "#5c6bc0" }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "22px" }}>{p.fullName || "Nalini S"}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "13px" }}>
                Employee ID: {p.employeeId || "TCH409"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "13px" }}>
                Class: {p.classAssigned || "Nilai08"} &nbsp;|&nbsp; Section: {p.section || "B"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />}
                sx={{ color: "#90caf9", borderColor: "#90caf9", textTransform: "none", borderRadius: "8px", "&:hover": { borderColor: "#bbdefb", color: "#bbdefb" } }}>
                Edit
              </Button>
              <Button variant="outlined" size="small" startIcon={<BlockIcon />}
                onClick={() => setDisableDialogOpen(true)}
                sx={{ color: "#ffb74d", borderColor: "#ffb74d", textTransform: "none", borderRadius: "8px", "&:hover": { borderColor: "#ffe082", color: "#ffe082" } }}>
                Disable
              </Button>
              <Button variant="outlined" size="small" startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ color: "#ef9a9a", borderColor: "#ef9a9a", textTransform: "none", borderRadius: "8px", "&:hover": { borderColor: "#ffcdd2", color: "#ffcdd2" } }}>
                Logout
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ─── STATS ROW ─── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          {
            label: "Attendance Today",
            value: p.attendanceToday || "Not Marked",
            chip: true,
            chipColor: p.attendanceToday === "Marked" ? "#4caf50" : "#f57c00",
            chipBg: p.attendanceToday === "Marked" ? "#e8f5e9" : "#fff3e0",
          },
          { label: "Total Students", value: p.totalStudents || 31 },
          { label: "Pending Task", value: p.pendingTasks || 2 },
          {
            label: "Class Assigned",
            value: p.classAssigned || "Nilai08",
            chip: true,
            chipColor: "#16a34a",
            chipBg: "#f0fdf4",
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>{stat.label}</Typography>
              {stat.chip ? (
                <Chip label={stat.value} size="small"
                  sx={{ backgroundColor: stat.chipBg, color: stat.chipColor, fontWeight: "bold", fontSize: "12px" }} />
              ) : (
                <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a2e" }}>{stat.value}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {/* ─── ATTENDANCE SECTION ─── */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 1.5 }}>Attendance</Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "#374151" }}>Today's Attendance</Typography>
              <Chip
                label={p.attendanceToday || "Not Marked"}
                size="small"
                sx={{
                  backgroundColor: p.attendanceToday === "Marked" ? "#e8f5e9" : "#fff3e0",
                  color: p.attendanceToday === "Marked" ? "#4caf50" : "#f57c00",
                  fontWeight: "bold", fontSize: "12px",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "13px", color: "#6B7280", mb: 2 }}>
              Last Marked: {formatDate(p.lastMarkedDate)}
            </Typography>
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => window.location.hash = "teacher-attendance"}
              sx={{ border: "1px solid #244C77", color: "#244C77", borderRadius: "8px", textTransform: "none", fontSize: "12px" }}
            >
              View History
            </Button>
          </Paper>

          {/* ─── ACADEMIC DETAILS ─── */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 1.5 }}>Academic Details</Typography>
            {[
              { label: "Subject", value: p.subject || "Tamil" },
              { label: "Class", value: p.classAssigned || "Nilai08" },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: "flex", py: 0.8, borderBottom: "1px solid #F3F4F6" }}>
                <Typography sx={{ fontSize: "13px", color: "#6B7280", width: 130, flexShrink: 0 }}>{label}</Typography>
                <Typography sx={{ fontSize: "13px", color: "#111827", fontWeight: 500 }}>{value || "—"}</Typography>
              </Box>
            ))}
            {[
              { label: "Google Classroom", url: p.googleClassroomUrl || "#" },
              { label: "Notebook", url: p.notebookUrl || "#" },
              { label: "Textbook", url: p.textbookUrl || "#" },
            ].map(({ label, url }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", py: 0.8, borderBottom: "1px solid #F3F4F6" }}>
                <Typography sx={{ fontSize: "13px", color: "#6B7280", width: 130, flexShrink: 0 }}>{label}</Typography>
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: "13px", color: "#244C77", display: "flex", alignItems: "center", gap: 0.5, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  Open <OpenInNewIcon sx={{ fontSize: "13px" }} />
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* ─── DOCUMENTS ─── */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 1.5 }}>Documents</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {[
                { label: "Lesson Plan", url: p.lessonPlanUrl || "#" },
                { label: "Study Materials", url: p.studyMaterialsUrl || "#" },
              ].map(({ label, url }) => (
                <Chip
                  key={label}
                  icon={<DescriptionIcon sx={{ fontSize: "16px !important", color: "#244C77 !important" }} />}
                  label={label}
                  size="small"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  sx={{ backgroundColor: "#EAF1F8", color: "#244C77", fontSize: "12px", fontWeight: 500 }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          {/* ─── STUDENT PERFORMANCE ─── */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 2 }}>Student Performance</Typography>
            <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
              <CircularScore value={p.classAverage || "82%"} label="Class Average" />
              <Box>
                <Box sx={{ display: "flex", py: 0.8, borderBottom: "1px solid #F3F4F6" }}>
                  <Typography sx={{ fontSize: "13px", color: "#6B7280", width: 140, flexShrink: 0 }}>Reports Submitted</Typography>
                  <Typography sx={{ fontSize: "13px", color: "#111827", fontWeight: 600 }}>{p.reportsSubmitted || 8}</Typography>
                </Box>
                <Box sx={{ display: "flex", py: 0.8 }}>
                  <Typography sx={{ fontSize: "13px", color: "#6B7280", width: 140, flexShrink: 0 }}>Pending Reports</Typography>
                  <Typography sx={{ fontSize: "13px", color: "#f57c00", fontWeight: 600 }}>{p.pendingReports || 2}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* ─── REQUEST & APPROVALS ─── */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 1.5 }}>Requests & Approvals</Typography>
            {[
              {
                label: "Leave Request",
                chipLabel: "1 Pending",
                chipColor: "#f57c00",
                chipBg: "#fff3e0",
                link: "teacher-leave",
              },
              {
                label: "Biliteracy Approvals",
                chipLabel: "2 to Review",
                chipColor: "#244C77",
                chipBg: "#f5f3ff",
                link: "teacher-biliteracy",
              },
            ].map(({ label, chipLabel, chipColor, chipBg, link }) => (
              <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: "1px solid #F3F4F6" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "13px", color: "#374151" }}>{label}:</Typography>
                  <Chip label={chipLabel} size="small"
                    sx={{ backgroundColor: chipBg, color: chipColor, fontWeight: "bold", fontSize: "11px" }} />
                </Box>
                <Typography
                  onClick={() => window.location.hash = link}
                  sx={{ fontSize: "12px", color: "#244C77", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  View →
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* ─── TEACHER DETAIL ─── */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 1.5 }}>Teacher Details</Typography>
            {[
              { label: "Phone Number", value: p.phone },
              { label: "Assigned Email", value: p.assignedEmail || p.email },
              { label: "Personal Email", value: p.personalEmail },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: "flex", py: 0.8, borderBottom: "1px solid #F3F4F6" }}>
                <Typography sx={{ fontSize: "13px", color: "#6B7280", width: 140, flexShrink: 0 }}>{label}</Typography>
                <Typography sx={{ fontSize: "13px", color: "#111827", fontWeight: 500, wordBreak: "break-all" }}>{value || "—"}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1.5 }} />
            {[
              { label: "Joined", value: formatDate(p.joiningDate) },
              { label: "Last Login", value: formatDate(p.lastLogin) },
              { label: "Created By", value: "Admin" },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: "flex", py: 0.6 }}>
                <Typography sx={{ fontSize: "12px", color: "#9CA3AF", width: 100, flexShrink: 0 }}>{label}</Typography>
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{value}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* ─── PROFILE UPDATE HISTORY (Audit Log) — §36 ─── */}
      {auditLog.length > 0 && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 2, mt: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <HistoryIcon sx={{ color: "#244C77", fontSize: "18px" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}>Profile Update History</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                  {["Date / Time", "Updated By", "Changed Fields", "Note"].map(h => (
                    <TableCell key={h} sx={{ fontSize: "12px", fontWeight: 700, color: "#6B7280", borderBottom: "1px solid #E5E7EB", py: 1 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLog.map((entry, idx) => (
                  <TableRow key={idx} sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover": { backgroundColor: "#f9fafb" } }}>
                    <TableCell sx={{ fontSize: "12px", color: "#374151", py: 1 }}>
                      {entry.updatedAt ? new Date(entry.updatedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", color: "#374151", py: 1 }}>{entry.updatedBy || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", color: "#244C77", py: 1 }}>
                      {Array.isArray(entry.changedFields) ? entry.changedFields.join(", ") : entry.changedFields || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", color: "#6B7280", py: 1 }}>{entry.note || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ─── DISABLE CONFIRMATION ─── */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Disable Profile</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#666", fontSize: "14px" }}>
            Are you sure you want to disable your teacher profile? This action will restrict access.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisableDialogOpen(false)}
            sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>
            Cancel
          </Button>
          <Button onClick={handleDisable}
            sx={{ backgroundColor: "#f59e0b", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#d97706" } }}>
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeacherProfile;
